/**
 * Utilidades para exportar reportes a PDF usando impresión del navegador
 */

/**
 * Genera estilos CSS para impresión
 */
const generarEstilosImpresion = () => {
  return `
    @media print {
      @page {
        size: landscape;
        margin: 1cm;
      }

      body {
        font-family: Arial, sans-serif;
        font-size: 10pt;
      }

      .reporte-header {
        text-align: center;
        margin-bottom: 20px;
        border-bottom: 2px solid #333;
        padding-bottom: 10px;
      }

      .reporte-header h1 {
        margin: 0;
        font-size: 18pt;
        color: #333;
      }

      .reporte-header .fecha {
        font-size: 9pt;
        color: #666;
        margin-top: 5px;
      }

      .reporte-filtros {
        margin-bottom: 15px;
        padding: 10px;
        background-color: #f5f5f5;
        border-left: 3px solid #1976d2;
      }

      .reporte-filtros h3 {
        margin: 0 0 5px 0;
        font-size: 11pt;
        color: #333;
      }

      .reporte-filtros p {
        margin: 2px 0;
        font-size: 9pt;
        color: #666;
      }

      .reporte-tabla {
        width: 100%;
        border-collapse: collapse;
        margin-top: 10px;
      }

      .reporte-tabla th {
        background-color: #1976d2;
        color: white;
        padding: 8px;
        text-align: left;
        font-size: 9pt;
        border: 1px solid #ddd;
      }

      .reporte-tabla td {
        padding: 6px 8px;
        border: 1px solid #ddd;
        font-size: 9pt;
      }

      .reporte-tabla tr:nth-child(even) {
        background-color: #f9f9f9;
      }

      .reporte-totales {
        margin-top: 15px;
        padding: 10px;
        background-color: #e3f2fd;
        border-left: 3px solid #1976d2;
      }

      .reporte-totales h3 {
        margin: 0 0 5px 0;
        font-size: 11pt;
        color: #333;
      }

      .reporte-totales p {
        margin: 3px 0;
        font-size: 9pt;
      }

      .reporte-footer {
        margin-top: 20px;
        text-align: center;
        font-size: 8pt;
        color: #999;
        border-top: 1px solid #ddd;
        padding-top: 10px;
      }

      .no-print {
        display: none !important;
      }
    }
  `;
};

/**
 * Genera HTML para el encabezado del reporte
 */
const generarEncabezado = (titulo, fecha) => {
  return `
    <div class="reporte-header">
      <h1>${titulo}</h1>
      <div class="fecha">Generado el: ${fecha}</div>
    </div>
  `;
};

/**
 * Genera HTML para los filtros aplicados
 */
const generarFiltros = (filtros) => {
  if (!filtros || Object.keys(filtros).length === 0) {
    return "";
  }

  let filtrosHTML = '<div class="reporte-filtros"><h3>Filtros Aplicados:</h3>';

  Object.entries(filtros).forEach(([key, value]) => {
    if (value) {
      const nombreFiltro = key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
      filtrosHTML += `<p><strong>${nombreFiltro}:</strong> ${value}</p>`;
    }
  });

  filtrosHTML += "</div>";
  return filtrosHTML;
};

/**
 * Genera HTML para una tabla
 */
const generarTabla = (columnas, datos) => {
  let tablaHTML = '<table class="reporte-tabla"><thead><tr>';

  // Encabezados
  columnas.forEach((columna) => {
    tablaHTML += `<th>${columna.header}</th>`;
  });

  tablaHTML += "</tr></thead><tbody>";

  // Filas de datos
  datos.forEach((fila) => {
    tablaHTML += "<tr>";
    columnas.forEach((columna) => {
      let valor = fila[columna.field];

      // Formatear valor según tipo
      if (columna.format) {
        valor = columna.format(valor, fila);
      }

      tablaHTML += `<td>${valor !== undefined && valor !== null ? valor : "-"}</td>`;
    });
    tablaHTML += "</tr>";
  });

  tablaHTML += "</tbody></table>";
  return tablaHTML;
};

/**
 * Genera HTML para los totales
 */
const generarTotales = (totales) => {
  if (!totales || Object.keys(totales).length === 0) {
    return "";
  }

  let totalesHTML = '<div class="reporte-totales"><h3>Totales:</h3>';

  Object.entries(totales).forEach(([key, value]) => {
    const nombreTotal = key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
    totalesHTML += `<p><strong>${nombreTotal}:</strong> ${value}</p>`;
  });

  totalesHTML += "</div>";
  return totalesHTML;
};

/**
 * Genera HTML para el pie de página
 */
const generarPieDePagina = () => {
  return `
    <div class="reporte-footer">
      <p>Sistema de Gestión de Procesos de Conciliación - ConciliaDireto</p>
      <p>Documento generado automáticamente - No requiere firma</p>
    </div>
  `;
};

/**
 * Exporta reporte a PDF usando impresión del navegador
 * @param {object} config - Configuración del reporte
 * @param {string} config.titulo - Título del reporte
 * @param {Array} config.columnas - Definición de columnas
 * @param {Array} config.datos - Datos a mostrar
 * @param {object} config.filtros - Filtros aplicados (opcional)
 * @param {object} config.totales - Totales a mostrar (opcional)
 */
export const exportarAPDF = (config) => {
  const { titulo, columnas, datos, filtros = {}, totales = {} } = config;

  const fecha = new Date().toLocaleString("es-CO", {
    dateStyle: "long",
    timeStyle: "short",
  });

  // Generar HTML completo
  const htmlCompleto = `
    <!DOCTYPE html>
    <html lang="es">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${titulo}</title>
      <style>
        ${generarEstilosImpresion()}
      </style>
    </head>
    <body>
      ${generarEncabezado(titulo, fecha)}
      ${generarFiltros(filtros)}
      ${generarTabla(columnas, datos)}
      ${generarTotales(totales)}
      ${generarPieDePagina()}
    </body>
    </html>
  `;

  // Crear ventana nueva para impresión
  const ventanaImpresion = window.open("", "_blank", "width=1200,height=800");

  if (ventanaImpresion) {
    ventanaImpresion.document.write(htmlCompleto);
    ventanaImpresion.document.close();

    // Esperar a que cargue y luego imprimir
    ventanaImpresion.onload = () => {
      setTimeout(() => {
        ventanaImpresion.print();
      }, 250);
    };

    return true;
  } else {
    alert("No se pudo abrir la ventana de impresión. Por favor, habilite las ventanas emergentes.");
    return false;
  }
};

/**
 * Formateadores comunes para columnas
 */
export const formatters = {
  moneda: (valor) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(valor);
  },

  numero: (valor) => {
    return new Intl.NumberFormat("es-CO").format(valor);
  },

  porcentaje: (valor) => {
    return `${valor} %`;
  },

  fecha: (valor) => {
    if (!valor) return "-";
    const fecha = new Date(valor);
    return fecha.toLocaleDateString("es-CO");
  },

  boolean: (valor) => {
    return valor ? "Sí" : "No";
  },

  truncar: (valor, maxLength = 30) => {
    if (!valor) return "-";
    const str = valor.toString();
    return str.length > maxLength ? str.substring(0, maxLength) + "..." : str;
  },
};
